import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAccount } from '../context/AccountContext';
import { useBrand } from '../context/BrandContext';
import { Skeleton } from '../components/Layout';
import { getAccounts, createAccount, updateAccount, suspendAccount, activateAccount, getPlans } from '../api/client';
import client from '../api/client';
import { C, Btn, Toast, useNotify } from '../utils/styles';

const inp = { width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', boxSizing:'border-box' };
const labelStyle = { fontSize:12, fontWeight:500, color:C.text, marginBottom:4, display:'block' };

const Modal = ({ title, icon, onClose, children, width=480 }) => (
  <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(15,15,15,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
    <div onClick={e=>e.stopPropagation()} style={{ width, maxWidth:'100%', maxHeight:'88vh', overflowY:'auto', background:'#fff', borderRadius:16, padding:24, boxShadow:'0 25px 60px rgba(0,0,0,0.3)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
        <div style={{ fontWeight:600, fontSize:15, color:C.dark, display:'flex', alignItems:'center', gap:8 }}>
          <i className={`ti ${icon}`} style={{ color:C.primary, fontSize:18 }} />{title}
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, fontSize:18, padding:4 }}><i className="ti ti-x" /></button>
      </div>
      {children}
    </div>
  </div>
);

const STATUS_COLORS = { active:'#22c55e', suspended:'#ef4444', cancelled:'#9ca3af' };

export default function AgencyDashboard({ tab: initialTab='accounts' }) {
  const { switchAccount } = useAuth();
  const { activeAccount, isWhiteLabel } = useAccount();
  const { brand, updateBrand } = useBrand();
  const [notification, notify] = useNotify();
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);

  // Sub-accounts
  const [subAccounts, setSubAccounts]  = useState([]);
  const [accsLoading, setAccsLoading]  = useState(true);
  const [plans,       setPlans]        = useState([]);
  const [showNew,     setShowNew]      = useState(false);
  const [newAcct,     setNewAcct]      = useState({ name:'', plan_id:'', owner_email:'', owner_name:'' });
  const [saving,      setSaving]       = useState(false);
  const [editAcct,    setEditAcct]     = useState(null);

  // Delete
  const [deleteAcct,        setDeleteAcct]        = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting,          setDeleting]          = useState(false);

  // Branding
  const [localBrand,  setLocalBrand]  = useState({ ...brand });
  const [brandSaving, setBrandSaving] = useState(false);

  // ── Custom domain state ───────────────────────────────────────
  const [customDomain,       setCustomDomain]       = useState(activeAccount?.custom_domain || '');
  const [domainSaving,       setDomainSaving]       = useState(false);
  const [domainVerifying,    setDomainVerifying]    = useState(false);
  const [domainVerifyResult, setDomainVerifyResult] = useState(null);

  useEffect(() => { fetchSubAccounts(); fetchPlans(); }, []); // eslint-disable-line
  useEffect(() => { setLocalBrand({ ...brand }); }, [brand]);
  useEffect(() => { setCustomDomain(activeAccount?.custom_domain || ''); }, [activeAccount]);

  const fetchSubAccounts = async () => {
    setAccsLoading(true);
    try {
      const { data } = await getAccounts();
      setSubAccounts((data||[]).filter(a => a.type==='sub'));
    } catch { notify('Failed to load sub-accounts','error'); }
    finally { setAccsLoading(false); }
  };

  const fetchPlans = async () => {
    try { const { data } = await getPlans(); setPlans((data||[]).filter(p=>p.is_active&&p.account_type==='sub')); }
    catch {}
  };

  const handleCreate = async () => {
    if (!newAcct.name) return notify('Account name is required','error');
    setSaving(true);
    try {
      await createAccount({ ...newAcct, type:'sub' });
      notify('Sub-account created! Invite sent to owner.');
      setShowNew(false);
      setNewAcct({ name:'', plan_id:'', owner_email:'', owner_name:'' });
      fetchSubAccounts();
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
    finally { setSaving(false); }
  };

  const handleSuspend  = async (id) => {
    if (!window.confirm('Suspend this sub-account?')) return;
    try { await suspendAccount(id); notify('Suspended'); fetchSubAccounts(); }
    catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };

  const handleActivate = async (id) => {
    try { await activateAccount(id); notify('Activated'); fetchSubAccounts(); }
    catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };

  const handleSwitchInto = (accountId) => {
    switchAccount(accountId);
    window.location.href = '/';
  };

  const openDeleteModal = (acc) => { setDeleteAcct(acc); setDeleteConfirmName(''); };

  const handleDeleteAccount = async () => {
    if (!deleteAcct || deleteConfirmName !== deleteAcct.name) return notify('Name does not match','error');
    setDeleting(true);
    try {
      await client.delete(`/api/accounts/${deleteAcct.id}`, { data: { confirmName: deleteConfirmName } });
      notify(`"${deleteAcct.name}" deleted permanently`);
      setDeleteAcct(null); setDeleteConfirmName('');
      fetchSubAccounts();
    } catch (err) { notify(err.response?.data?.error||'Failed to delete','error'); }
    finally { setDeleting(false); }
  };

  const handleSaveBrand = async () => {
    setBrandSaving(true);
    try {
      await client.post('/api/settings/brand', localBrand);
      updateBrand(localBrand);
      notify('Branding saved!');
    } catch { notify('Failed to save branding','error'); }
    finally { setBrandSaving(false); }
  };

  // ── Custom domain handlers ────────────────────────────────────
  const handleSaveDomain = async () => {
    const clean = customDomain.toLowerCase().replace(/^https?:\/\//,'').replace(/\/.*$/,'').trim();
    if (!clean) return notify('Please enter a domain','error');
    setDomainSaving(true);
    try {
      await client.patch('/api/accounts/me', { custom_domain: clean });
      setCustomDomain(clean);
      setDomainVerifyResult(null);
      notify('Domain saved! Now click Verify DNS once propagation is complete.');
    } catch (err) { notify(err.response?.data?.error||'Failed to save domain','error'); }
    finally { setDomainSaving(false); }
  };

  const handleVerifyDomain = async () => {
    const clean = customDomain.toLowerCase().replace(/^https?:\/\//,'').replace(/\/.*$/,'').trim();
    if (!clean) return notify('Please enter a domain first','error');
    setDomainVerifying(true);
    setDomainVerifyResult(null);
    try {
      const { data } = await client.post('/api/accounts/verify-domain', { domain: clean });
      setDomainVerifyResult(data);
      if (data.verified) notify('Domain verified and saved!');
    } catch (err) { notify(err.response?.data?.error||'Verification failed','error'); }
    finally { setDomainVerifying(false); }
  };

  const TABS = [
    { id:'accounts', label:'Sub-Accounts', icon:'ti-building' },
    ...(isWhiteLabel ? [
      { id:'branding', label:'Branding',      icon:'ti-palette' },
      { id:'domain',   label:'Custom Domain', icon:'ti-world'   },
    ] : []),
  ];

  const activeCount    = subAccounts.filter(a=>a.status==='active').length;
  const suspendedCount = subAccounts.filter(a=>a.status==='suspended').length;

  return (
    <div>
      <Toast {...notification} />

      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:700, color:C.dark, marginBottom:2 }}>Agency Dashboard</h1>
        <p style={{ fontSize:13, color:C.muted }}>{activeAccount?.name} · {subAccounts.length} sub-accounts</p>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Total',     value:subAccounts.length, icon:'ti-building',     color:C.primary  },
          { label:'Active',    value:activeCount,         icon:'ti-circle-check', color:'#22c55e'  },
          { label:'Suspended', value:suspendedCount,      icon:'ti-pause',        color:'#f59e0b'  },
        ].map(s => (
          <div key={s.label} style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:'16px 20px', display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:40, height:40, borderRadius:10, background:`${s.color}18`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <i className={`ti ${s.icon}`} style={{ fontSize:20, color:s.color }} />
            </div>
            <div>
              <div style={{ fontSize:22, fontWeight:700, color:C.dark }}>{s.value}</div>
              <div style={{ fontSize:12, color:C.muted }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      {TABS.length > 1 && (
        <div style={{ display:'flex', gap:4, marginBottom:20, background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:6 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px 12px', borderRadius:8, border:'none', background:activeTab===t.id?C.primary:'transparent', color:activeTab===t.id?'#fff':C.muted, fontSize:13, fontWeight:activeTab===t.id?600:400, cursor:'pointer' }}>
              <i className={`ti ${t.icon}`} style={{ fontSize:15 }} />{t.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Sub-accounts ── */}
      {activeTab==='accounts' && (
        <div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
            <Btn onClick={() => setShowNew(true)}><i className="ti ti-plus" /> Add Sub-Account</Btn>
          </div>
          <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden' }}>
            {accsLoading ? Array(3).fill(null).map((_,i) => (
              <div key={i} style={{ padding:'14px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:12 }}>
                <Skeleton circle size={40} /><div style={{ flex:1 }}><Skeleton height={12} width="40%" style={{ marginBottom:6 }} /><Skeleton height={10} width="55%" /></div><Skeleton height={30} width={70} radius={6} />
              </div>
            )) : subAccounts.length===0 ? (
              <div style={{ padding:40, textAlign:'center', color:C.faint, fontSize:13 }}>
                <i className="ti ti-building" style={{ fontSize:32, color:C.border, display:'block', marginBottom:10 }} />No sub-accounts yet.
              </div>
            ) : subAccounts.map((acc,i) => (
              <div key={acc.id} style={{ padding:'14px 20px', borderBottom:i<subAccounts.length-1?`1px solid ${C.border}`:'none', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:40, height:40, borderRadius:10, background:C.primary, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
                  {acc.logo_url
                    ? <img src={acc.logo_url} alt={acc.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : <span style={{ color:'#fff', fontSize:16, fontWeight:700 }}>{acc.name?.charAt(0).toUpperCase()}</span>
                  }
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:C.dark, marginBottom:3 }}>{acc.name}</div>
                  <div style={{ fontSize:11, color:C.muted }}>
                    {acc.plan?.name && <span>Plan: <b>{acc.plan.name}</b> · </span>}
                    Created {new Date(acc.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                  </div>
                </div>
                <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:`${STATUS_COLORS[acc.status]||'#9ca3af'}18`, color:STATUS_COLORS[acc.status]||'#9ca3af', flexShrink:0 }}>
                  {acc.status}
                </span>
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <button onClick={() => handleSwitchInto(acc.id)} title="Enter account"
                    style={{ padding:'5px 10px', borderRadius:6, border:`1px solid ${C.primary}`, background:'var(--color-primary-light)', color:C.primary, cursor:'pointer', fontSize:12, display:'flex', alignItems:'center', gap:4 }}>
                    <i className="ti ti-login" style={{ fontSize:13 }} /> Enter
                  </button>
                  <button onClick={() => setEditAcct({...acc})} style={{ padding:'5px 8px', borderRadius:6, border:'none', background:'var(--color-primary-light)', color:C.primary, cursor:'pointer', fontSize:13 }}>
                    <i className="ti ti-edit" />
                  </button>
                  {acc.status==='suspended'
                    ? <button onClick={()=>handleActivate(acc.id)} style={{ padding:'5px 8px', borderRadius:6, border:'none', background:'#f0fdf4', color:'#22c55e', cursor:'pointer', fontSize:13 }}><i className="ti ti-player-play" /></button>
                    : <button onClick={()=>handleSuspend(acc.id)} style={{ padding:'5px 8px', borderRadius:6, border:'none', background:'#fef3c7', color:'#d97706', cursor:'pointer', fontSize:13 }}><i className="ti ti-player-pause" /></button>
                  }
                  <button onClick={() => openDeleteModal(acc)} title="Delete permanently"
                    style={{ padding:'5px 8px', borderRadius:6, border:'none', background:'#fef2f2', color:C.danger, cursor:'pointer', fontSize:13 }}>
                    <i className="ti ti-trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Branding ── */}
      {activeTab==='branding' && (
        <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:24 }}>
          <div style={{ fontWeight:600, fontSize:15, color:C.dark, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
            <i className="ti ti-palette" style={{ color:C.primary }} /> White-label Branding
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div><label style={labelStyle}>App Name</label><input style={inp} value={localBrand.app_name||''} onChange={e=>setLocalBrand({...localBrand,app_name:e.target.value})} /></div>
            <div><label style={labelStyle}>Tagline</label><input style={inp} value={localBrand.tagline||''} onChange={e=>setLocalBrand({...localBrand,tagline:e.target.value})} /></div>
            <div><label style={labelStyle}>Logo URL</label><input style={inp} value={localBrand.logo_url||''} onChange={e=>setLocalBrand({...localBrand,logo_url:e.target.value})} /></div>
            <div><label style={labelStyle}>Login Logo URL</label><input style={inp} value={localBrand.login_logo_url||''} onChange={e=>setLocalBrand({...localBrand,login_logo_url:e.target.value})} /></div>
            <div><label style={labelStyle}>Favicon URL</label><input style={inp} value={localBrand.favicon_url||''} onChange={e=>setLocalBrand({...localBrand,favicon_url:e.target.value})} /></div>
            <div>
              <label style={labelStyle}>Primary Color</label>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input type="color" value={localBrand.primary_color||'#0099FF'} onChange={e=>setLocalBrand({...localBrand,primary_color:e.target.value})} style={{ width:44, height:36, borderRadius:8, border:`1px solid ${C.border}`, cursor:'pointer', padding:2 }} />
                <input style={{ flex:1, ...inp }} value={localBrand.primary_color||''} onChange={e=>setLocalBrand({...localBrand,primary_color:e.target.value})} />
              </div>
            </div>
          </div>
          <Btn onClick={handleSaveBrand} disabled={brandSaving}><i className="ti ti-device-floppy" /> {brandSaving?'Saving...':'Save Branding'}</Btn>
        </div>
      )}

      {/* ── Custom Domain ── */}
      {activeTab==='domain' && (
        <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:24 }}>
          <div style={{ fontWeight:600, fontSize:15, color:C.dark, marginBottom:8, display:'flex', alignItems:'center', gap:8 }}>
            <i className="ti ti-world" style={{ color:C.primary }} /> Custom Domain Setup
          </div>
          <p style={{ fontSize:13, color:C.muted, marginBottom:16 }}>
            Point your own domain to this CRM so your clients see your brand URL instead of <code>app.digitalxtension.com</code>.
          </p>

          {/* Steps */}
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
            {[
              { step:1, title:'Choose a subdomain',           desc:'e.g. crm.youragency.com — use a subdomain, not your root domain' },
              { step:2, title:'Add a CNAME record in DNS',    desc:'Log into your domain registrar (GoDaddy, Cloudflare, Namecheap) and add the record below.' },
              { step:3, title:'Wait for propagation',         desc:'DNS changes take anywhere from a few minutes to 48 hours.' },
              { step:4, title:'Enter your domain and verify', desc:'Once DNS is live, enter it below and click Verify DNS.' },
            ].map(s => (
              <div key={s.step} style={{ display:'flex', gap:12, padding:'10px 14px', background:'#f9fafb', borderRadius:10, border:`1px solid ${C.border}` }}>
                <div style={{ width:26, height:26, borderRadius:'50%', background:C.primary, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:12, fontWeight:700, flexShrink:0 }}>{s.step}</div>
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:C.dark, marginBottom:2 }}>{s.title}</div>
                  <div style={{ fontSize:12, color:C.muted }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* DNS record */}
          <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:10, padding:'14px 16px', marginBottom:20 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#0369a1', marginBottom:10 }}>DNS Record to Add</div>
            <div style={{ display:'grid', gridTemplateColumns:'80px 1fr 2fr', gap:8, fontSize:12 }}>
              <div style={{ fontWeight:600, color:'#0369a1' }}>Type</div>
              <div style={{ fontWeight:600, color:'#0369a1' }}>Name / Host</div>
              <div style={{ fontWeight:600, color:'#0369a1' }}>Value / Points To</div>
              <div style={{ color:'#0c4a6e' }}>CNAME</div>
              <div style={{ color:'#0c4a6e' }}>crm <span style={{ color:'#9ca3af' }}>(your subdomain)</span></div>
              <code style={{ color:'#0c4a6e', fontSize:11 }}>app.digitalxtension.com</code>
            </div>
          </div>

          {/* Current verified domain */}
          {activeAccount?.custom_domain && !domainVerifyResult && (
            <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'10px 14px', marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'#166534' }}>Current Verified Domain</div>
              <div style={{ fontSize:14, color:'#166534', fontWeight:500, marginTop:2 }}>{activeAccount.custom_domain}</div>
            </div>
          )}

          {/* Verify result */}
          {domainVerifyResult && (
            <div style={{ background:domainVerifyResult.verified?'#f0fdf4':'#fef2f2', border:`1px solid ${domainVerifyResult.verified?'#bbf7d0':'#fecaca'}`, borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:600, color:domainVerifyResult.verified?'#166534':'#991b1b', marginBottom:4 }}>
                {domainVerifyResult.message}
              </div>
              {domainVerifyResult.cname && (
                <div style={{ fontSize:11, color:domainVerifyResult.verified?'#166534':'#7f1d1d' }}>
                  CNAME found: <code>{domainVerifyResult.cname}</code>
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom:14 }}>
            <label style={labelStyle}>Your Custom Domain</label>
            <input style={inp} placeholder="crm.youragency.com"
              value={customDomain}
              onChange={e => { setCustomDomain(e.target.value); setDomainVerifyResult(null); }} />
            <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>Enter just the domain — no https:// prefix.</div>
          </div>

          <div style={{ display:'flex', gap:8 }}>
            <Btn onClick={handleSaveDomain} disabled={domainSaving || !customDomain.trim()}>
              <i className="ti ti-device-floppy" /> {domainSaving ? 'Saving...' : 'Save Domain'}
            </Btn>
            <Btn variant="outline" onClick={handleVerifyDomain} disabled={domainVerifying || !customDomain.trim()}>
              <i className={`ti ${domainVerifying?'ti-loader ti-spin':'ti-refresh'}`} /> {domainVerifying ? 'Checking DNS...' : 'Verify DNS'}
            </Btn>
          </div>
          <div style={{ marginTop:10, fontSize:11, color:C.faint }}>
            Use <a href="https://dnschecker.org" target="_blank" rel="noreferrer" style={{ color:C.primary }}>dnschecker.org</a> to check if your CNAME is live globally before verifying.
          </div>
        </div>
      )}

      {/* ── Create sub-account modal ── */}
      {showNew && (
        <Modal title="Add Sub-Account" icon="ti-building" onClose={() => setShowNew(false)}>
          <div style={{ display:'grid', gap:12, marginBottom:16 }}>
            <div><label style={labelStyle}>Account Name *</label><input style={inp} placeholder="Client Business Name" value={newAcct.name} onChange={e=>setNewAcct({...newAcct,name:e.target.value})} /></div>
            <div>
              <label style={labelStyle}>Plan</label>
              <select style={inp} value={newAcct.plan_id} onChange={e=>setNewAcct({...newAcct,plan_id:e.target.value})}>
                <option value="">No plan</option>
                {plans.map(p=><option key={p.id} value={p.id}>{p.name} — ₹{p.price_monthly_inr}/mo</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Owner Email <span style={{ color:C.faint, fontWeight:400 }}>(invite sent)</span></label><input style={inp} type="email" placeholder="client@example.com" value={newAcct.owner_email} onChange={e=>setNewAcct({...newAcct,owner_email:e.target.value})} /></div>
            <div><label style={labelStyle}>Owner Name <span style={{ color:C.faint, fontWeight:400 }}>(optional)</span></label><input style={inp} placeholder="John Doe" value={newAcct.owner_name} onChange={e=>setNewAcct({...newAcct,owner_name:e.target.value})} /></div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Btn onClick={handleCreate} disabled={saving}><i className="ti ti-plus" /> {saving?'Creating...':'Create'}</Btn>
            <Btn variant="outline" onClick={() => setShowNew(false)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* ── Edit sub-account modal ── */}
      {editAcct && (
        <Modal title={`Edit — ${editAcct.name}`} icon="ti-edit" onClose={() => setEditAcct(null)}>
          <div style={{ display:'grid', gap:12, marginBottom:16 }}>
            <div><label style={labelStyle}>Account Name</label><input style={inp} value={editAcct.name} onChange={e=>setEditAcct({...editAcct,name:e.target.value})} /></div>
            <div>
              <label style={labelStyle}>Plan</label>
              <select style={inp} value={editAcct.plan_id||''} onChange={e=>setEditAcct({...editAcct,plan_id:e.target.value})}>
                <option value="">No plan</option>
                {plans.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inp} value={editAcct.status} onChange={e=>setEditAcct({...editAcct,status:e.target.value})}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Btn onClick={async () => {
              try { await updateAccount(editAcct.id,{name:editAcct.name,plan_id:editAcct.plan_id,status:editAcct.status}); notify('Updated!'); setEditAcct(null); fetchSubAccounts(); }
              catch(err){ notify(err.response?.data?.error||'Failed','error'); }
            }}><i className="ti ti-device-floppy" /> Save</Btn>
            <Btn variant="outline" onClick={() => setEditAcct(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* ── Delete confirmation modal ── */}
      {deleteAcct && (
        <Modal title="Delete Sub-Account" icon="ti-alert-triangle" onClose={() => { setDeleteAcct(null); setDeleteConfirmName(''); }}>
          <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:14, marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'#991b1b', marginBottom:6 }}>⚠️ This action is permanent and cannot be undone.</div>
            <div style={{ fontSize:12, color:'#7f1d1d', lineHeight:1.6 }}>
              Deleting <strong>{deleteAcct.name}</strong> will permanently remove all leads, campaigns, bookings, team members, and all data. This cannot be recovered.
            </div>
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ ...labelStyle, marginBottom:8 }}>
              To confirm, type the account name exactly: <strong style={{ color:C.danger }}>{deleteAcct.name}</strong>
            </label>
            <input style={{ ...inp, borderColor:deleteConfirmName&&deleteConfirmName!==deleteAcct.name?C.danger:C.border }}
              placeholder="Type account name to confirm..."
              value={deleteConfirmName}
              onChange={e => setDeleteConfirmName(e.target.value)}
              onKeyDown={e => e.key==='Enter' && handleDeleteAccount()}
            />
            {deleteConfirmName && deleteConfirmName!==deleteAcct.name && (
              <div style={{ fontSize:11, color:C.danger, marginTop:4 }}>Name does not match</div>
            )}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={handleDeleteAccount} disabled={deleting||deleteConfirmName!==deleteAcct.name}
              style={{ padding:'9px 16px', borderRadius:8, border:'none', background:deleteConfirmName===deleteAcct.name?C.danger:'#f3f4f6', color:deleteConfirmName===deleteAcct.name?'#fff':'#9ca3af', fontSize:13, fontWeight:600, cursor:deleting||deleteConfirmName!==deleteAcct.name?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:6 }}>
              <i className={`ti ${deleting?'ti-loader ti-spin':'ti-trash'}`} /> {deleting?'Deleting...':'Delete Permanently'}
            </button>
            <Btn variant="outline" onClick={() => { setDeleteAcct(null); setDeleteConfirmName(''); }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
